import { useMemo, useState } from "react"

import { createFilterOptions } from "@mui/material/Autocomplete"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { AddIcon } from "@l2r-front/l2r-icons"
import { PropTypes } from "@l2r-front/l2r-proptypes"

import { I18N_NAMESPACE } from "../../constants/i18n"

import { L2RButton } from "../L2RButton"

import * as Styled from "./ChipAdd.styled"

const filter = createFilterOptions()

export const ChipAdd = (props) => {

    const [value, setValue] = useState(null)
    const [displayInput, setDisplayInput] = useState(false)

    const {
        className,
        chipList,
        disabled,
        id,
        onValueSelected,
        options,
        small,
        variant,
        values,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)

    const displayedAddableChip = useMemo(() => {
        return options.filter(addableChip => {
            return chipList.every(chip => chip !== addableChip)
        })
    }, [chipList, options])

    if (displayInput) {
        return (
            <Styled.Autocomplete
                clearOnBlur
                value={value}
                freeSolo
                handleHomeEndKeys
                openOnFocus
                options={displayedAddableChip}
                onClose={() => {
                    setDisplayInput(false)
                }}
                selectOnFocus
                onChange={(_, newValue) => {
                    if (typeof newValue === "string") {
                        setValue({
                            title: newValue,
                        })
                        onValueSelected(newValue)
                        setDisplayInput(false)
                    } else if (newValue && newValue.inputValue) {
                        setValue({
                            title: newValue.inputValue,
                        })
                        onValueSelected(newValue.inputValue)
                        setDisplayInput(false)
                    } else {
                        setValue(newValue)
                    }
                }}
                slotProps={{
                    listbox: {
                        style: {
                            wordBreak: "break-all",
                        },
                    },
                }}
                filterOptions={(options, params) => {
                    const filtered = filter(options, params)
                    const { inputValue } = params
                    const existingValues = values.map(v => v.label?.toLowerCase())
                    const existingTags = existingValues.concat(options)
                    const isExisting = existingTags.some((option) => {
                        const optionValue = typeof option === "string" ? option.toLowerCase()
                            : option.title?.toLocaleLowerCase()
                        return inputValue.toLocaleLowerCase() === optionValue
                    })

                    if (inputValue !== "" && !isExisting) {
                        filtered.push({
                            inputValue,
                            title: `${t(I18N_NAMESPACE, "components.chipAdd.add")} "${inputValue}"`,
                        })
                    }

                    return filtered
                }}
                getOptionLabel={(option) => {
                    if (typeof option === "string") {
                        return option
                    }
                    return option.title
                }}
                renderInput={(params) => (
                    <Styled.TextField
                        {...params}
                        autoFocus
                        color="primary"
                        placeholder={t(I18N_NAMESPACE, "components.chipAdd.placeholder")}
                        slotProps={{
                            input: {
                                ...params.InputProps,
                                style: Styled.TextFieldInputStyle,
                            },
                            inputLabel: { style: Styled.TextFieldInputLabelStyle },
                            htmlInput: {
                                ...params.inputProps,
                                maxLength: 25,
                            },
                        }}
                        small={small}
                        variant="outlined"
                    />
                )}
            />
        )
    }

    return (
        <L2RButton
            className={className}
            disabled={disabled}
            id={id}
            shape="circle"
            size={small ? "small" : "medium"}
            variant={variant === "outlined" ? "secondary" : "primary"}
            onClick={() => {
                setDisplayInput(v => !v)
                setValue(null)
            }}>
            <AddIcon />
        </L2RButton>
    )
}

ChipAdd.propTypes = {
    chipList: PropTypes.arrayOf(PropTypes.string),
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    onValueSelected: PropTypes.func.isRequired,
    options: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    small: PropTypes.bool,
    variant: PropTypes.oneOf(["outlined", "contained"]),
    values: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
    })),
}

ChipAdd.defaultProps = {
    disabled: false,
}
